import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import {
  FileText, Plus, Trash2, Search, Package, Download, Save, Copy,
  ArrowLeft, Building, DollarSign, Percent, ShoppingCart, Eye,
  ClipboardList, Loader2, Check
} from 'lucide-react';
import {
  listContractors, listProducts, listOffers, createOffer, getOffer,
  updateOffer, deleteOffer, generateOfferPdf, duplicateOffer, getPipelineDeals
} from '../lib/api';

// ===== Offer List View =====
function OfferList({ lang, tenantId, onCreateNew, onEdit }) {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => { loadOffers(); }, [tenantId, filter]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadOffers() {
    setLoading(true);
    try {
      const status = filter === 'all' ? null : filter;
      const data = await listOffers(tenantId, null, null, status);
      setOffers(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  async function handleDelete(id) {
    if (!window.confirm(lang === 'pl' ? 'Usunac oferte?' : 'Delete this offer?')) return;
    try {
      await deleteOffer(id);
      toast.success(lang === 'pl' ? 'Oferta usunieta' : 'Offer deleted');
      loadOffers();
    } catch (err) { toast.error('Error'); }
  }

  async function handleDuplicate(id) {
    try {
      const newOffer = await duplicateOffer(id);
      toast.success(lang === 'pl' ? 'Oferta skopiowana' : 'Offer duplicated');
      onEdit(newOffer.id);
    } catch (err) { toast.error('Error'); }
  }

  async function handleGeneratePdf(id) {
    try {
      const blob = await generateOfferPdf(id);
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      window.open(url, '_blank');
      toast.success(lang === 'pl' ? 'PDF wygenerowany' : 'PDF generated');
      loadOffers();
    } catch (err) { toast.error('Error generating PDF'); }
  }

  const statusColors = {
    draft: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
    sent: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
    accepted: 'bg-green-500/10 text-green-500 border-green-500/20',
    rejected: 'bg-red-500/10 text-red-500 border-red-500/20',
  };
  const statusLabels = {
    draft: lang === 'pl' ? 'Szkic' : 'Draft',
    sent: lang === 'pl' ? 'Wyslana' : 'Sent',
    accepted: lang === 'pl' ? 'Zaakceptowana' : 'Accepted',
    rejected: lang === 'pl' ? 'Odrzucona' : 'Rejected',
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
            {lang === 'pl' ? 'Kreator Ofert' : 'Offer Creator'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {offers.length} {lang === 'pl' ? 'ofert' : 'offers'}
          </p>
        </div>
        <Button className="gap-2" onClick={onCreateNew} data-testid="create-offer-button">
          <Plus className="w-4 h-4" />
          {lang === 'pl' ? 'Nowa oferta' : 'New Offer'}
        </Button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2">
        {['all', 'draft', 'sent', 'accepted', 'rejected'].map(f => (
          <Button key={f} variant={filter === f ? 'default' : 'outline'} size="sm"
            onClick={() => setFilter(f)} className="text-xs">
            {f === 'all' ? (lang === 'pl' ? 'Wszystkie' : 'All') : (statusLabels[f] || f)}
          </Button>
        ))}
      </div>

      <Card className="border border-border">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-16 w-full" />)}</div>
          ) : offers.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardList className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                {lang === 'pl' ? 'Brak ofert. Stworz pierwsza oferte!' : 'No offers. Create your first offer!'}
              </p>
              <Button variant="outline" className="mt-4 gap-2" onClick={onCreateNew}>
                <Plus className="w-4 h-4" />
                {lang === 'pl' ? 'Nowa oferta' : 'New Offer'}
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{lang === 'pl' ? 'Nr oferty' : 'Offer #'}</TableHead>
                  <TableHead>{lang === 'pl' ? 'Tytul' : 'Title'}</TableHead>
                  <TableHead>{lang === 'pl' ? 'Kontrahent' : 'Contractor'}</TableHead>
                  <TableHead>{lang === 'pl' ? 'Wartosc' : 'Value'}</TableHead>
                  <TableHead>{lang === 'pl' ? 'Status' : 'Status'}</TableHead>
                  <TableHead>{lang === 'pl' ? 'Data' : 'Date'}</TableHead>
                  <TableHead className="text-right">{lang === 'pl' ? 'Akcje' : 'Actions'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {offers.map(o => (
                  <TableRow key={o.id} className="hover:bg-white/[0.02] cursor-pointer" onClick={() => onEdit(o.id)}>
                    <TableCell className="font-mono text-xs text-muted-foreground">{o.offer_number}</TableCell>
                    <TableCell className="text-sm font-medium">{o.title}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Building className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="text-sm">{o.contractor_name}</span>
                        {o.contractor_company && (
                          <span className="text-xs text-muted-foreground">({o.contractor_company})</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm font-semibold text-primary">
                      {(o.total || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2 })} {o.currency}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-xs ${statusColors[o.status] || ''}`}>
                        {statusLabels[o.status] || o.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {o.created_at ? new Date(o.created_at).toLocaleDateString('pl-PL') : '-'}
                    </TableCell>
                    <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => handleGeneratePdf(o.id)} title="PDF">
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDuplicate(o.id)} title={lang === 'pl' ? 'Duplikuj' : 'Duplicate'}>
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(o.id)}
                          className="text-muted-foreground hover:text-destructive" title={lang === 'pl' ? 'Usun' : 'Delete'}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ===== Product Picker Dialog =====
function ProductPicker({ lang, tenantId, open, onClose, onSelect, selectedIds }) {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) loadProducts();
  }, [open, search]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadProducts() {
    setLoading(true);
    try {
      const res = await listProducts(tenantId, search || null, null, 0, 500);
      setProducts(res.products || res);
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            {lang === 'pl' ? 'Wybierz produkty z katalogu' : 'Select products from catalog'}
          </DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder={lang === 'pl' ? 'Szukaj po nazwie, SKU, kategorii...' : 'Search by name, SKU, category...'}
            className="pl-9"
          />
        </div>
        <div className="overflow-y-auto flex-1 -mx-2 px-2" style={{ maxHeight: '50vh' }}>
          {loading ? (
            <div className="space-y-2 py-2">{[1,2,3,4].map(i => <Skeleton key={i} className="h-16 w-full" />)}</div>
          ) : products.length === 0 ? (
            <div className="text-center py-8">
              <Package className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">{lang === 'pl' ? 'Brak produktow' : 'No products found'}</p>
            </div>
          ) : (
            <div className="space-y-1.5 py-2">
              {products.map(p => {
                const isSelected = selectedIds.includes(p.id);
                return (
                  <div key={p.id}
                    onClick={() => !isSelected && onSelect(p)}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                      isSelected
                        ? 'bg-primary/5 border-primary/30 opacity-60 cursor-default'
                        : 'border-border hover:bg-white/[0.04] hover:border-primary/20'
                    }`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-muted/50 flex items-center justify-center flex-shrink-0">
                      <Package className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">{p.name}</span>
                        {p.sku && <span className="text-xs text-muted-foreground font-mono">{p.sku}</span>}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {p.category && <Badge variant="secondary" className="text-xs py-0">{p.category}</Badge>}
                        {p.base_price > 0 && (
                          <span className="text-xs text-primary font-medium">{p.base_price} {p.currency}/{p.unit}</span>
                        )}
                        {p.stock_quantity > 0 && (
                          <span className="text-xs text-muted-foreground">
                            {lang === 'pl' ? 'Stan' : 'Stock'}: {p.stock_quantity} {p.stock_unit || p.unit}
                          </span>
                        )}
                      </div>
                    </div>
                    {isSelected ? (
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    ) : (
                      <Plus className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===== Offer Editor =====
function OfferEditor({ lang, tenantId, offerId, onBack }) {
  const [contractors, setContractors] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [productPickerOpen, setProductPickerOpen] = useState(false);

  const [form, setForm] = useState({
    contractor_id: '',
    deal_id: '',
    title: '',
    items: [],
    terms: '',
    validity_days: 30,
    notes: '',
    currency: 'PLN',
    payment_terms: '',
    delivery_terms: '',
  });
  const [existingOffer, setExistingOffer] = useState(null);

  useEffect(() => { loadInitialData(); }, [tenantId, offerId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadInitialData() {
    setLoading(true);
    try {
      const [ct, dl] = await Promise.all([
        listContractors(tenantId),
        getPipelineDeals(tenantId)
      ]);
      setContractors(ct);
      setDeals(dl);

      if (offerId) {
        const offer = await getOffer(offerId);
        setExistingOffer(offer);
        setForm({
          contractor_id: offer.contractor_id || '',
          deal_id: offer.deal_id || '',
          title: offer.title || '',
          items: offer.items || [],
          terms: offer.terms || '',
          validity_days: offer.validity_days || 30,
          notes: offer.notes || '',
          currency: offer.currency || 'PLN',
          payment_terms: offer.payment_terms || '',
          delivery_terms: offer.delivery_terms || '',
        });
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  // Product selection handler
  function handleProductSelect(product) {
    const newItem = {
      product_id: product.id,
      name: product.name,
      sku: product.sku || '',
      quantity: product.min_order || 1,
      unit: product.unit || 'szt',
      unit_price: product.base_price || 0,
      currency: product.currency || form.currency,
      discount_percent: 0,
      notes: '',
    };
    setForm(prev => ({ ...prev, items: [...prev.items, newItem] }));
    setProductPickerOpen(false);
    toast.success(lang === 'pl' ? `Dodano: ${product.name}` : `Added: ${product.name}`);
  }

  // Add manual item (not from catalog)
  function addManualItem() {
    setForm(prev => ({
      ...prev,
      items: [...prev.items, {
        product_id: '', name: '', sku: '', quantity: 1,
        unit: 'szt', unit_price: 0, currency: form.currency,
        discount_percent: 0, notes: ''
      }]
    }));
  }

  function updateItem(index, field, value) {
    setForm(prev => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, items };
    });
  }

  function removeItem(index) {
    setForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  }

  // Calculate totals
  const calculateLineTotal = (item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.unit_price) || 0;
    const discount = parseFloat(item.discount_percent) || 0;
    return qty * price * (1 - discount / 100);
  };

  const subtotal = form.items.reduce((sum, item) => sum + calculateLineTotal(item), 0);

  async function handleSave() {
    if (!form.contractor_id) {
      toast.error(lang === 'pl' ? 'Wybierz kontrahenta' : 'Select a contractor');
      return;
    }
    if (form.items.length === 0) {
      toast.error(lang === 'pl' ? 'Dodaj przynajmniej jeden produkt' : 'Add at least one product');
      return;
    }
    setSaving(true);
    try {
      if (existingOffer) {
        await updateOffer(existingOffer.id, form);
        toast.success(lang === 'pl' ? 'Oferta zapisana' : 'Offer saved');
      } else {
        const newOffer = await createOffer(form, tenantId);
        setExistingOffer(newOffer);
        toast.success(lang === 'pl' ? 'Oferta utworzona' : 'Offer created');
      }
    } catch (err) { toast.error('Error saving offer'); }
    setSaving(false);
  }

  async function handleGeneratePdf() {
    if (!existingOffer) {
      // Save first
      if (!form.contractor_id || form.items.length === 0) {
        toast.error(lang === 'pl' ? 'Najpierw uzupelnij i zapisz oferte' : 'Fill in and save the offer first');
        return;
      }
      setSaving(true);
      try {
        const newOffer = await createOffer(form, tenantId);
        setExistingOffer(newOffer);
        setGeneratingPdf(true);
        const blob = await generateOfferPdf(newOffer.id);
        const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        window.open(url, '_blank');
        toast.success(lang === 'pl' ? 'PDF wygenerowany' : 'PDF generated');
      } catch (err) { toast.error('Error'); }
      setSaving(false);
      setGeneratingPdf(false);
      return;
    }

    // Save changes first, then generate
    setGeneratingPdf(true);
    try {
      await updateOffer(existingOffer.id, form);
      const blob = await generateOfferPdf(existingOffer.id);
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      window.open(url, '_blank');
      toast.success(lang === 'pl' ? 'PDF wygenerowany' : 'PDF generated');
    } catch (err) { toast.error('Error generating PDF'); }
    setGeneratingPdf(false);
  }

  // Deal selection handler - auto-fill contractor
  function handleDealSelect(dealId) {
    const deal = deals.find(d => d.id === dealId);
    if (deal) {
      setForm(prev => ({
        ...prev,
        deal_id: dealId,
        contractor_id: deal.contractor_id || prev.contractor_id,
        title: prev.title || deal.title || '',
        currency: deal.currency || prev.currency,
      }));
    }
  }

  const selectedProductIds = form.items.map(i => i.product_id).filter(Boolean);

  if (loading) {
    return <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-24 w-full" />)}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="gap-1">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
              {existingOffer
                ? (lang === 'pl' ? `Edycja oferty ${existingOffer.offer_number}` : `Edit Offer ${existingOffer.offer_number}`)
                : (lang === 'pl' ? 'Nowa oferta' : 'New Offer')
              }
            </h1>
            {existingOffer && (
              <p className="text-sm text-muted-foreground mt-0.5">
                {lang === 'pl' ? 'Status' : 'Status'}: <Badge variant="outline" className="text-xs ml-1">{existingOffer.status}</Badge>
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {lang === 'pl' ? 'Zapisz szkic' : 'Save Draft'}
          </Button>
          <Button className="gap-2" onClick={handleGeneratePdf} disabled={generatingPdf}>
            {generatingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {lang === 'pl' ? 'Generuj PDF' : 'Generate PDF'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Left Column - Main Form */}
        <div className="col-span-2 space-y-6">
          {/* Contractor & Deal Selection */}
          <Card className="border border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Building className="w-4 h-4" />
                {lang === 'pl' ? 'Odbiorca oferty' : 'Offer Recipient'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{lang === 'pl' ? 'Kontrahent' : 'Contractor'} *</Label>
                  <Select value={form.contractor_id} onValueChange={v => setForm(p => ({ ...p, contractor_id: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder={lang === 'pl' ? 'Wybierz kontrahenta...' : 'Select contractor...'} /></SelectTrigger>
                    <SelectContent>
                      {contractors.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name} {c.company ? `(${c.company})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{lang === 'pl' ? 'Powiazany deal (opcjonalnie)' : 'Linked Deal (optional)'}</Label>
                  <Select value={form.deal_id || 'none'} onValueChange={v => {
                    if (v === 'none') setForm(p => ({ ...p, deal_id: '' }));
                    else handleDealSelect(v);
                  }}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder={lang === 'pl' ? 'Wybierz deal...' : 'Select deal...'} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{lang === 'pl' ? '-- Brak --' : '-- None --'}</SelectItem>
                      {deals.map(d => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.title} ({d.contractor_name})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">{lang === 'pl' ? 'Tytul oferty' : 'Offer Title'}</Label>
                <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                  placeholder={lang === 'pl' ? 'np. Oferta na oswietlenie biura' : 'e.g. Office lighting offer'}
                  className="mt-1" />
              </div>
            </CardContent>
          </Card>

          {/* Items Table */}
          <Card className="border border-border">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4" />
                  {lang === 'pl' ? 'Pozycje oferty' : 'Offer Items'}
                  <Badge variant="secondary" className="text-xs">{form.items.length}</Badge>
                </CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={addManualItem}>
                    <Plus className="w-3.5 h-3.5" />
                    {lang === 'pl' ? 'Reczna pozycja' : 'Manual Item'}
                  </Button>
                  <Button size="sm" className="gap-1.5 text-xs" onClick={() => setProductPickerOpen(true)}>
                    <Package className="w-3.5 h-3.5" />
                    {lang === 'pl' ? 'Z katalogu' : 'From Catalog'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {form.items.length === 0 ? (
                <div className="text-center py-10 border-2 border-dashed border-border rounded-xl">
                  <ShoppingCart className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {lang === 'pl' ? 'Dodaj produkty z katalogu lub recznie' : 'Add products from catalog or manually'}
                  </p>
                  <div className="flex gap-2 justify-center mt-3">
                    <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setProductPickerOpen(true)}>
                      <Package className="w-3.5 h-3.5" />
                      {lang === 'pl' ? 'Przegladaj katalog' : 'Browse Catalog'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-0">
                  {/* Table Header */}
                  <div className="grid grid-cols-12 gap-2 px-2 py-2 text-xs font-medium text-muted-foreground border-b border-border">
                    <div className="col-span-4">{lang === 'pl' ? 'Produkt' : 'Product'}</div>
                    <div className="col-span-1 text-right">{lang === 'pl' ? 'Ilosc' : 'Qty'}</div>
                    <div className="col-span-1">{lang === 'pl' ? 'Jedn.' : 'Unit'}</div>
                    <div className="col-span-2 text-right">{lang === 'pl' ? 'Cena jedn.' : 'Unit Price'}</div>
                    <div className="col-span-1 text-right">{lang === 'pl' ? 'Rabat %' : 'Disc %'}</div>
                    <div className="col-span-2 text-right">{lang === 'pl' ? 'Wartosc' : 'Value'}</div>
                    <div className="col-span-1"></div>
                  </div>

                  {/* Items */}
                  {form.items.map((item, idx) => {
                    const lineTotal = calculateLineTotal(item);
                    return (
                      <div key={idx} className="grid grid-cols-12 gap-2 px-2 py-2 items-center border-b border-border/50 hover:bg-white/[0.02] group">
                        <div className="col-span-4">
                          <Input value={item.name} onChange={e => updateItem(idx, 'name', e.target.value)}
                            placeholder={lang === 'pl' ? 'Nazwa produktu' : 'Product name'}
                            className="h-8 text-sm" />
                          {item.sku && <span className="text-xs text-muted-foreground font-mono ml-1">{item.sku}</span>}
                        </div>
                        <div className="col-span-1">
                          <Input type="number" value={item.quantity} min="0" step="1"
                            onChange={e => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                            className="h-8 text-sm text-right" />
                        </div>
                        <div className="col-span-1">
                          <Select value={item.unit} onValueChange={v => updateItem(idx, 'unit', v)}>
                            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {['szt', 'kg', 't', 'm3', 'l', 'm', 'opak', 'kpl'].map(u => (
                                <SelectItem key={u} value={u}>{u}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2">
                          <Input type="number" value={item.unit_price} min="0" step="0.01"
                            onChange={e => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                            className="h-8 text-sm text-right" />
                        </div>
                        <div className="col-span-1">
                          <Input type="number" value={item.discount_percent} min="0" max="100" step="1"
                            onChange={e => updateItem(idx, 'discount_percent', parseFloat(e.target.value) || 0)}
                            className="h-8 text-sm text-right" />
                        </div>
                        <div className="col-span-2 text-right">
                          <span className="text-sm font-semibold text-primary">
                            {lineTotal.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                          <span className="text-xs text-muted-foreground ml-1">{form.currency}</span>
                        </div>
                        <div className="col-span-1 text-right">
                          <Button variant="ghost" size="sm" onClick={() => removeItem(idx)}
                            className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}

                  {/* Total Row */}
                  <div className="grid grid-cols-12 gap-2 px-2 py-3 items-center bg-muted/30 rounded-b-lg">
                    <div className="col-span-9 text-right text-sm font-semibold">
                      {lang === 'pl' ? 'RAZEM:' : 'TOTAL:'}
                    </div>
                    <div className="col-span-2 text-right">
                      <span className="text-lg font-bold text-primary">
                        {subtotal.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <span className="text-sm text-muted-foreground ml-1">{form.currency}</span>
                    </div>
                    <div className="col-span-1"></div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Terms & Notes */}
          <Card className="border border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">
                {lang === 'pl' ? 'Warunki i uwagi' : 'Terms & Notes'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{lang === 'pl' ? 'Warunki platnosci' : 'Payment Terms'}</Label>
                  <Input value={form.payment_terms}
                    onChange={e => setForm(p => ({ ...p, payment_terms: e.target.value }))}
                    placeholder={lang === 'pl' ? 'np. 30 dni, przelew' : 'e.g. 30 days, bank transfer'}
                    className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">{lang === 'pl' ? 'Warunki dostawy' : 'Delivery Terms'}</Label>
                  <Input value={form.delivery_terms}
                    onChange={e => setForm(p => ({ ...p, delivery_terms: e.target.value }))}
                    placeholder={lang === 'pl' ? 'np. DDP Warszawa, 14 dni' : 'e.g. DDP Warsaw, 14 days'}
                    className="mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs">{lang === 'pl' ? 'Dodatkowe warunki' : 'Additional Terms'}</Label>
                <Textarea value={form.terms}
                  onChange={e => setForm(p => ({ ...p, terms: e.target.value }))}
                  placeholder={lang === 'pl' ? 'Dodatkowe warunki handlowe...' : 'Additional commercial terms...'}
                  className="mt-1 min-h-[80px]" />
              </div>
              <div>
                <Label className="text-xs">{lang === 'pl' ? 'Uwagi' : 'Notes'}</Label>
                <Textarea value={form.notes}
                  onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                  placeholder={lang === 'pl' ? 'Notatki wewnetrzne lub dla klienta...' : 'Internal or client notes...'}
                  className="mt-1 min-h-[60px]" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Summary */}
        <div className="space-y-4">
          {/* Summary Card */}
          <Card className="border border-border sticky top-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                {lang === 'pl' ? 'Podsumowanie' : 'Summary'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{lang === 'pl' ? 'Pozycji' : 'Items'}:</span>
                  <span className="font-medium">{form.items.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{lang === 'pl' ? 'Kontrahent' : 'Contractor'}:</span>
                  <span className="font-medium text-right max-w-[140px] truncate">
                    {contractors.find(c => c.id === form.contractor_id)?.name || '-'}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-sm font-medium">{lang === 'pl' ? 'Wartosc netto' : 'Net Value'}:</span>
                  <span className="text-lg font-bold text-primary">
                    {subtotal.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} {form.currency}
                  </span>
                </div>
              </div>

              <Separator />

              {/* Settings */}
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">{lang === 'pl' ? 'Waluta' : 'Currency'}</Label>
                    <Select value={form.currency} onValueChange={v => setForm(p => ({ ...p, currency: v }))}>
                      <SelectTrigger className="mt-1 h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['PLN', 'EUR', 'USD', 'GBP', 'CHF'].map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">{lang === 'pl' ? 'Waznosc (dni)' : 'Validity (days)'}</Label>
                    <Input type="number" value={form.validity_days} min="1"
                      onChange={e => setForm(p => ({ ...p, validity_days: parseInt(e.target.value) || 30 }))}
                      className="mt-1 h-8" />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Actions */}
              <div className="space-y-2">
                <Button className="w-full gap-2" onClick={handleGeneratePdf} disabled={generatingPdf || form.items.length === 0}>
                  {generatingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  {lang === 'pl' ? 'Generuj PDF' : 'Generate PDF'}
                </Button>
                <Button variant="outline" className="w-full gap-2" onClick={handleSave} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {lang === 'pl' ? 'Zapisz szkic' : 'Save Draft'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Quick Tips */}
          <Card className="border border-border bg-primary/5">
            <CardContent className="p-4">
              <p className="text-xs font-medium text-primary mb-2">
                {lang === 'pl' ? 'Wskazowki' : 'Tips'}
              </p>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• {lang === 'pl' ? 'Dodaj produkty z katalogu, aby automatycznie uzupelnic ceny' : 'Add products from catalog to auto-fill prices'}</li>
                <li>• {lang === 'pl' ? 'Ustaw rabat procentowy per pozycja' : 'Set percentage discount per item'}</li>
                <li>• {lang === 'pl' ? 'Powiaz oferte z dealem w pipeline' : 'Link offer to a deal in pipeline'}</li>
                <li>• {lang === 'pl' ? 'PDF mozesz wygenerowac wielokrotnie' : 'You can generate PDF multiple times'}</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Product Picker Dialog */}
      <ProductPicker
        lang={lang} tenantId={tenantId}
        open={productPickerOpen}
        onClose={() => setProductPickerOpen(false)}
        onSelect={handleProductSelect}
        selectedIds={selectedProductIds}
      />
    </div>
  );
}

// ===== Main OfferCreator Component =====
export default function OfferCreator({ lang, tenantId }) {
  const [searchParams] = useSearchParams();
  const [view, setView] = useState('list'); // 'list' | 'editor'
  const [editingOfferId, setEditingOfferId] = useState(null);

  // Check URL params for deal_id or offer_id to open editor directly
  useEffect(() => {
    const dealId = searchParams.get('deal_id');
    const offerId = searchParams.get('offer_id');
    if (offerId) {
      setEditingOfferId(offerId);
      setView('editor');
    } else if (dealId) {
      // Create new offer linked to deal
      setEditingOfferId(null);
      setView('editor');
    }
  }, [searchParams]);

  function handleCreateNew() {
    setEditingOfferId(null);
    setView('editor');
  }

  function handleEdit(offerId) {
    setEditingOfferId(offerId);
    setView('editor');
  }

  function handleBack() {
    setView('list');
    setEditingOfferId(null);
  }

  if (view === 'editor') {
    return <OfferEditor lang={lang} tenantId={tenantId} offerId={editingOfferId} onBack={handleBack} />;
  }

  return <OfferList lang={lang} tenantId={tenantId} onCreateNew={handleCreateNew} onEdit={handleEdit} />;
}
